import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { BiPlus, BiTrash, BiSave, BiDownload, BiUpload, BiCalendarPlus, BiEraser } from "react-icons/bi";

import { getJrLineDetail, saveJrLineDetail } from "../job-request/jrV2Slice";
import { downloadTemplate as dlTemplate, parseEntriesFromExcel } from "./entryImport";
import QtyInput, { formatQty } from "./QtyInput";

const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
const numOrNull = (v) => (v === "" || v == null || isNaN(parseFloat(v)) ? null : parseFloat(v));
// Tanggal lokal (bukan toISOString/UTC yang mundur sehari sebelum jam 07/08 pagi).
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
// Default "Tanggal" baris baru = tanggal kerja JR: hari ini bila masih di bulan
// kerja, selain itu tanggal kerja JR (MJS merekap per bulan kerja, bukan tanggal input).
const defaultEntryDate = (periodMonth, workDate) => {
  if (!periodMonth || today().startsWith(periodMonth)) return today();
  return String(workDate || "").startsWith(periodMonth) ? String(workDate).slice(0, 10) : periodMonth + "-01";
};
const thisMonth = () => today().slice(0, 7);

// Semua tanggal (YYYY-MM-DD) dalam satu bulan "YYYY-MM" — dirakit sebagai
// string agar tak bergeser sehari karena zona waktu (sama dgn GeneralServiceEntryEditor).
function monthDates(ym) {
  const [y, m] = String(ym || "").split("-").map(Number);
  if (!y || !m || m < 1 || m > 12) return [];
  const lastDay = new Date(y, m, 0).getDate();
  return Array.from({ length: lastDay }, (_, i) =>
    `${ym}-${String(i + 1).padStart(2, "0")}`);
}

// "YYYY-MM-DD" + 1 hari, tetap sebagai string (lewat Date UTC agar aman zona waktu).
function nextDate(d) {
  const t = new Date(`${d}T00:00:00Z`);
  t.setUTCDate(t.getUTCDate() + 1);
  return t.toISOString().slice(0, 10);
}

function monthLabel(ym) {
  const [y, m] = String(ym || "").split("-").map(Number);
  if (!y || !m) return ym || "";
  return new Date(y, m - 1, 1).toLocaleDateString("id-ID", { month: "long", year: "numeric" });
}

// Parse tanggal "naif" (tanpa zona) SELALU sebagai UTC, sama dengan backend
// (parseFlexTime di jr_linedetail.go). `new Date("2026-10-31")` dibaca UTC tetapi
// `new Date("2026-10-01T00:00")` dibaca waktu lokal browser; selisih keduanya
// (mis. Mulai hasil muat ulang vs Selesai hasil ketik) bergeser sebesar offset
// zona → 1–31 Okt terhitung 30 hari + 7 jam = 31 hari = 2 bulan, padahal backend 1.
function parseNaif(v) {
  const s = String(v || "").trim();
  if (!s) return new Date(NaN);
  if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(s)) return new Date(s); // sudah punya zona
  return new Date(s.length <= 10 ? `${s}T00:00:00Z` : `${s}Z`);
}

function hoursBetween(a, b) {
  const da = parseNaif(a), db = parseNaif(b);
  if (isNaN(da.getTime()) || isNaN(db.getTime())) return 0;
  return Math.max(0, Math.ceil((db - da) / 3600000));
}

function daysBetween(a, b) {
  const da = parseNaif(a), db = parseNaif(b);
  if (isNaN(da.getTime()) || isNaN(db.getTime())) return 0;
  return Math.max(0, Math.ceil((db - da) / 86400000));
}

// Dasar kuantitas untuk SEMUA formula: nilai terbesar antara Qty dan Dimensi,
// TANPA pembulatan (qty desimal ditagih apa adanya, sejak 2026-10-13).
// Mirror backend effectiveQty — lihat komentar lengkapnya di jr_linedetail.go.
// Dimensi adalah ukuran ALTERNATIF (konvensi revenue ton / W-M: 1 ton = 1 m3),
// bukan tambahan; MAX diambil PER BARIS ENTRI.
const effQty = (e) => Math.max(num(e.Qty), num(e.Dimensi));

// Dimensi (Port Services) bukan input manual — turunan Length × Height × Weight.
// Mirror backend: jr_linedetail.go, SaveJrLineDetail meng-override Dimensi
// dengan rumus yang sama untuk ActivityName "PORT_SERVICES".
const calcDimensi = (e) => num(e.Length) * num(e.Height) * num(e.Weight);

// Hitung "jumlah" (BilledQty) — mirror backend computeBilledQty.
export function calcBilledPR(formula, e) {
  switch (formula) {
    case "DURATION_HOURS": {
      const h = hoursBetween(e.DateStart, e.DateEnd);
      return (String(e.ActivityType || "").toLowerCase() === "standby" && h > 4) ? 4 : h;
    }
    case "DURATION_DAYS": // Vessel satuan Day: rentang hari (ceil per 24 jam), tanpa Qty
      return daysBetween(e.DateStart, e.DateEnd);
    case "DURATION_HOURS_X_QTY":
      return hoursBetween(e.DateStart, e.DateEnd) * (effQty(e) || 1);
    case "METER": {
      // Pakai selisih meter bila terisi; jika tidak, pakai Qty/Dimensi langsung.
      const ms = numOrNull(e.MeterStart), me = numOrNull(e.MeterEnd);
      if (ms == null || me == null) return effQty(e);
      const d = me - ms; return d > 0 ? d : 0;
    }
    case "DURATION": // Area Rental berbasis hari (× Luas/Qty, default 1)
      return daysBetween(e.DateStart, e.DateEnd) * (effQty(e) || 1);
    case "DURATION_MONTHS": // Area Rental berbasis bulan = CEIL(hari/30) × Qty
      return Math.ceil(daysBetween(e.DateStart, e.DateEnd) / 30) * (effQty(e) || 1);
    case "QTY_X_MULT": // Office: Ukuran (Multiplier) × Unit (Qty) × 1 periode
      return effQty(e) * (num(e.Multiplier) || 1);
    default: return effQty(e);
  }
}

// "2026-09-01..." → "September 2026" (periode bulan; dipakai Summary & cetak).
export function fmtPeriode(v) {
  const m = String(v || "").match(/^(\d{4})-(\d{2})/);
  if (!m) return v || "—";
  return new Date(Number(m[1]), Number(m[2]) - 1, 1)
    .toLocaleDateString("id-ID", { month: "long", year: "numeric" });
}

// Kolom Length/Height/Weight + Dimensi (turunan, read-only) + satuannya.
// Dipasang pada preset Port Service. Sengaja dipisah jadi konstanta agar
// keempat preset memakai definisi yang sama. Default 0 — lihat blankRow().
const DIMENSI_COLS = [
  { key: "Length",     label: "Length", type: "number" },
  { key: "Height",     label: "Height", type: "number" },
  { key: "Weight",     label: "Weight", type: "number" },
  { key: "Dimensi",    label: "Dimensi", type: "computed" },
  { key: "DimensiUom", label: "UoM Dimensi", type: "uomSelect" },
];

// Konfigurasi kolom per service. attr:true → disimpan ke Attributes (JSONB).
const VESSEL = { formula: "DURATION_HOURS", cols: [
  { key: "Jetty",        label: "Jetty",       type: "text",           attr: true },
  { key: "VesselName",   label: "Vessel Name", type: "text",           attr: true },
  { key: "DateStart",    label: "Arrival",     type: "datetime-local" },
  { key: "DateEnd",      label: "Departure",   type: "datetime-local" },
  { key: "ActivityType", label: "Activity",    type: "select", attr: true, options: ["Loading", "Unloading", "Standby"] },
  ...DIMENSI_COLS,
  { key: "Remarks",      label: "Keterangan",  type: "text" },
] };
const VESSEL_DAY = { ...VESSEL, formula: "DURATION_DAYS" };
const CARGO = { formula: "QTY", cols: [
  { key: "MaterialDesc", label: "Material",    type: "text",   attr: true },
  { key: "Packaging",    label: "Packaging",   type: "text",   attr: true },
  { key: "Qty",          label: "Quantity",    type: "number" },
  { key: "Uom",          label: "UoM",         type: "uom" },
  ...DIMENSI_COLS,
  { key: "Destination",  label: "Destination", type: "text",   attr: true },
  { key: "Remarks",      label: "Keterangan",  type: "text" },
] };
const RENTAL = { formula: "DURATION_HOURS_X_QTY", cols: [
  { key: "UnitDetail", label: "Detail Unit",  type: "text",          attr: true },
  { key: "UnitNumber", label: "Unit Number",  type: "text",          attr: true },
  { key: "Qty",        label: "Qty",          type: "number" },
  { key: "Uom",        label: "UoM",          type: "uom" },
  { key: "DateStart",  label: "Start",        type: "datetime-local" },
  { key: "DateEnd",    label: "End",          type: "datetime-local" },
  { key: "Remarks",    label: "Keterangan",   type: "text" },
] };

// Bulk / Liquid Transfer — pakai meter (selisih) atau Qty langsung.
const BULK = { formula: "METER", cols: [
  { key: "EntryDate",    label: "Tanggal",     type: "date" },
  { key: "MaterialDesc", label: "Material",    type: "text",   attr: true },
  { key: "MeterStart",   label: "Meter Awal",  type: "number" },
  { key: "MeterEnd",     label: "Meter Akhir", type: "number" },
  { key: "Qty",          label: "Quantity",    type: "number" },
  { key: "Uom",          label: "UoM",         type: "uom" },
  ...DIMENSI_COLS,
  { key: "Remarks",      label: "Keterangan",  type: "text" },
] };

// Staging area — per kuantitas.
const STAGING = { formula: "QTY", cols: [
  { key: "EntryDate", label: "Tanggal",     type: "date" },
  { key: "AreaRef",   label: "Area/Lokasi", type: "text",   attr: true },
  { key: "Qty",       label: "Quantity",    type: "number" },
  { key: "Uom",       label: "UoM",         type: "uom" },
  ...DIMENSI_COLS,
  { key: "Remarks",   label: "Keterangan",  type: "text" },
] };

// Equipment (alat) — jam × qty.
const EQUIPMENT = { formula: "DURATION_HOURS_X_QTY", cols: [
  { key: "_item",     label: "Unit / Alat", type: "label" },
  { key: "DateStart", label: "Start",       type: "datetime-local" },
  { key: "DateEnd",   label: "End",         type: "datetime-local" },
  { key: "Qty",       label: "Qty",         type: "number" },
  { key: "Uom",       label: "UoM",         type: "uom" },
  { key: "Remarks",   label: "Keterangan",  type: "text" },
] };

// Labour (tenaga) — orang × jam.
const LABOUR = { formula: "DURATION_HOURS_X_QTY", cols: [
  { key: "_item",     label: "Jenis Tenaga",  type: "label" },
  { key: "DateStart", label: "Start",         type: "datetime-local" },
  { key: "DateEnd",   label: "End",           type: "datetime-local" },
  { key: "Qty",       label: "Jumlah Orang",  type: "number" },
  { key: "Uom",       label: "UoM",           type: "uom" },
  { key: "Remarks",   label: "Keterangan",    type: "text" },
] };

// Truck Entry — per trip.
const TRUCK = { formula: "QTY", cols: [
  { key: "EntryDate",   label: "Tanggal",            type: "date" },
  { key: "PlateNumber", label: "No. Polisi/Deskripsi", type: "text", attr: true },
  { key: "Qty",         label: "Trip",               type: "number" },
  { key: "Uom",         label: "UoM",                type: "uom" },
  { key: "Remarks",     label: "Keterangan",         type: "text" },
] };

const ACT_FORM = {
  Vessel_Service: VESSEL, Vessel_Berthing: VESSEL,
  Cargodoring: CARGO, Stevedoring: CARGO, Stavedoring: CARGO, Receiving: CARGO,
  Receiving_Using_Forklift: CARGO, Receiving_Using_Crane: CARGO,
  Delivery: CARGO, Delivery_Using_Forklift: CARGO, Delivery_Using_Crane: CARGO,
  Stacking: CARGO, Stacking_atau_Restacking: CARGO,
  Stacking_using_crane: CARGO, Stacking_using_forklift: CARGO,
  Stuffing: CARGO, Bundling: CARGO,
  Bulk_Transfer: BULK, Liquid: BULK,
  Staging_for_loading: STAGING,
};

// Form item pendukung dikenali dari LineGroup (ActivityName kosong).
const GROUP_FORM = { EQUIPMENT, LABOUR, TRUCK };

// === Area Rental — form ditentukan dari UoM (dasar penagihan). ===
// Form A (durasi ± luas): hari atau bulan × Luas/Qty (default 1).
const AREA_DAY = { formula: "DURATION", cols: [
  { key: "_item",     label: "Area",         type: "label" },
  { key: "Qty",       label: "Luas/Volume",  type: "number" },
  { key: "DateStart", label: "Mulai",        type: "date" },
  { key: "DateEnd",   label: "Selesai",      type: "date" },
  { key: "Remarks",   label: "Keterangan",   type: "text" },
] };
const AREA_MONTH = { formula: "DURATION_MONTHS", cols: AREA_DAY.cols };
// Form B/C (kuantitas / lump sum): Qty langsung.
const AREA_QTY = { formula: "QTY", cols: [
  { key: "_item",     label: "Area",         type: "label" },
  { key: "EntryDate", label: "Tanggal",      type: "date" },
  { key: "Qty",       label: "Qty",          type: "number" },
  { key: "Uom",       label: "UoM",          type: "uom" },
  { key: "Remarks",   label: "Keterangan",   type: "text" },
] };

// === Rental — form ditentukan dari UoM (jam/hari/bulan/qty), unit tetap ditampilkan. ===
const RENTAL_DAY = { formula: "DURATION", cols: [
  { key: "UnitDetail", label: "Detail Unit", type: "text", attr: true },
  { key: "UnitNumber", label: "Unit Number", type: "text", attr: true },
  { key: "Qty",        label: "Qty",         type: "number" },
  { key: "DateStart",  label: "Mulai",       type: "date" },
  { key: "DateEnd",    label: "Selesai",     type: "date" },
  { key: "Remarks",    label: "Keterangan",  type: "text" },
] };
const RENTAL_MONTH = { formula: "DURATION_MONTHS", cols: RENTAL_DAY.cols };
const RENTAL_QTY = { formula: "QTY", cols: [
  { key: "UnitDetail", label: "Detail Unit", type: "text", attr: true },
  { key: "UnitNumber", label: "Unit Number", type: "text", attr: true },
  { key: "EntryDate",  label: "Tanggal",     type: "date" },
  { key: "Qty",        label: "Qty",         type: "number" },
  { key: "Uom",        label: "UoM",         type: "uom" },
  { key: "Remarks",    label: "Keterangan",  type: "text" },
] };

// Builder preset UoM-aware (Manpower / Waste / TWS): pilih formula + kolom dari UoM line.
//   jam → datetime (jam×qty) ; hari/bulan → date (durasi×qty) ; lainnya → kuantitas.
function uomAwareCfg(line, { itemLabel, qtyLabel = "Qty", attrCols = [] }) {
  const u = String(line.SatuanNama || line.SatuanID || "").toLowerCase();
  const base = [{ key: "_item", label: itemLabel, type: "label" }, ...attrCols];
  const ket = { key: "Remarks", label: "Keterangan", type: "text" };
  if (u.includes("hour"))
    return { formula: "DURATION_HOURS_X_QTY", cols: [...base,
      { key: "Qty", label: qtyLabel, type: "number" },
      { key: "DateStart", label: "Mulai", type: "datetime-local" },
      { key: "DateEnd", label: "Selesai", type: "datetime-local" }, ket] };
  if (u.includes("month") || u.includes("day"))
    return { formula: u.includes("month") ? "DURATION_MONTHS" : "DURATION", cols: [...base,
      { key: "Qty", label: qtyLabel, type: "number" },
      { key: "DateStart", label: "Mulai", type: "date" },
      { key: "DateEnd", label: "Selesai", type: "date" }, ket] };
  return { formula: "QTY", cols: [...base,
    { key: "EntryDate", label: "Tanggal", type: "date" },
    { key: "Qty", label: qtyLabel, type: "number" },
    { key: "Uom", label: "UoM", type: "uom" }, ket] };
}

// Office (MAreaRental AR0005): satu entri = satu periode bulan. Ukuran (m²) hanya
// ada bila satuan Quotation berbasis luas; satuan Month cukup Unit. Mirror backend
// AreaRentalOfficeID / areaUomUsesLuas (jr_linedetail.go).
export const AREA_OFFICE_ID = "AR0005";
export const areaUomUsesLuas = (u) => /m²|m2|m\^2|sqm|persegi/i.test(String(u || ""));
const OFFICE_PERIODE = { key: "EntryDate", label: "Periode", type: "month" };
const OFFICE_UNIT = { key: "Qty", label: "Unit (Jumlah)", type: "number" };
const OFFICE_KET = { key: "Remarks", label: "Keterangan", type: "text" };
const OFFICE_LUAS = { formula: "QTY_X_MULT", cols: [
  { key: "_item", label: "Area", type: "label" },
  { key: "Multiplier", label: "Ukuran (m²)", type: "number" },
  OFFICE_UNIT, OFFICE_PERIODE, OFFICE_KET,
] };
const OFFICE_UNIT_ONLY = { formula: "QTY_X_MULT", cols: [
  { key: "_item", label: "Area", type: "label" },
  OFFICE_UNIT, OFFICE_PERIODE, OFFICE_KET,
] };

export function cfgForLine(line) {
  // Office: form periode bulan (Ukuran × Unit atau Unit saja, sesuai UoM).
  if (line.ActivityName === "AREA_RENTAL" && line.ItemName === AREA_OFFICE_ID)
    return areaUomUsesLuas(line.SatuanNama || line.SatuanID) ? OFFICE_LUAS : OFFICE_UNIT_ONLY;
  // Area Rental: pilih form berdasarkan UoM line (Month/Day/lainnya).
  if (line.ActivityName === "AREA_RENTAL") {
    const u = String(line.SatuanNama || line.SatuanID || "").toLowerCase();
    if (u.includes("month")) return AREA_MONTH;
    if (u.includes("day")) return AREA_DAY;
    return AREA_QTY;
  }
  // Vessel Service/Berthing: hanya Day atau Hour mengikuti satuan Quotation.
  if (line.ItemName === "Vessel_Berthing" || line.ItemName === "Vessel_Service") {
    const u = String(line.SatuanNama || line.SatuanID || "").toLowerCase();
    return u.includes("day") ? VESSEL_DAY : VESSEL;
  }
  // Rental: UoM-aware — jam (datetime) / hari / bulan / kuantitas.
  if (line.ActivityName === "RENTAL") {
    const u = String(line.SatuanNama || line.SatuanID || "").toLowerCase();
    if (u.includes("hour")) return RENTAL;        // jam × qty (datetime-local)
    if (u.includes("month")) return RENTAL_MONTH; // bulan × qty
    if (u.includes("day")) return RENTAL_DAY;     // hari × qty
    return RENTAL_QTY;                            // count/trip/set/dll
  }
  // Manpower: UoM-aware (Durasi × Orang / Kuantitas).
  if (line.ActivityName === "MANPOWER")
    return uomAwareCfg(line, { itemLabel: "Jenis Tenaga", qtyLabel: "Jumlah Orang" });
  // Waste Management: penanganan limbah, UoM-aware (+ No. Manifest).
  if (line.ActivityName === "WASTE_MANAGEMENT_SERVICES")
    return uomAwareCfg(line, { itemLabel: "Jenis Limbah", qtyLabel: "Berat/Volume",
      attrCols: [{ key: "ManifestNo", label: "No. Manifest", type: "text", attr: true }] });
  // Temporary Waste Storage (item pendukung Waste): UoM-aware.
  if (line.LineGroup === "TWS")
    return uomAwareCfg(line, { itemLabel: "Lokasi Penyimpanan" });
  if (GROUP_FORM[line.LineGroup]) return GROUP_FORM[line.LineGroup];
  return ACT_FORM[line.ItemName] || CARGO;
}

function blankRow(entryDate = today()) {
  return { EntryDate: entryDate, Qty: "", Remarks: "", Length: 0, Height: 0, Weight: 0, Dimensi: 0 };
}

export default function PortRentalEntryEditor({ line, jrID, uomOptions, period, workDate, onSaved }) {
  const dispatch = useDispatch();
  const cfg = cfgForLine(line);
  const itemLabel = line.ItemDisplay || line.ItemName || "—";

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // "Isi 1 Bulan" / "Hapus Kosong" — khusus Area Rental (Layanan Tetap, ikut JR
  // General Service bulanan). Bulan dikunci ke Period JR bila ada, sama seperti
  // GeneralServiceEntryEditor; pilihan bebas hanya fallback JR tanpa Period.
  const isAreaRental = line.ActivityName === "AREA_RENTAL";
  const periodMonth = /^\d{4}-\d{2}$/.test((period || "").slice(0, 7)) ? period.slice(0, 7) : "";
  const [genMonth, setGenMonth] = useState(() => periodMonth || thisMonth());
  const effMonth = periodMonth || genMonth;

  const load = useCallback(() => {
    setLoading(true);
    dispatch(getJrLineDetail(line.JrLineID)).unwrap()
      // spread Attributes ke level entri agar mudah diedit di form
      .then(res => setEntries((res?.data ?? []).map(d => ({ ...d, ...(d.Attributes || {}) }))))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [dispatch, line.JrLineID]);

  useEffect(() => { load(); }, [load]);

  const addRow = () => setEntries(p => [...p, blankRow(defaultEntryDate(periodMonth, workDate))]);
  const delRow = (i) => setEntries(p => p.filter((_, idx) => idx !== i));
  const upd = (i, k, v) => setEntries(p => p.map((e, idx) => {
    if (idx !== i) return e;
    const next = { ...e, [k]: v };
    // Length/Height/Weight berubah → Dimensi (read-only) ikut dihitung ulang.
    if (k === "Length" || k === "Height" || k === "Weight") next.Dimensi = calcDimensi(next);
    return next;
  }));

  // Bentuk baris hasil generate ditentukan formula (lihat cfgForLine/AREA_*):
  //   QTY             → 1 baris per tanggal (EntryDate), tinggal isi Qty.
  //   DURATION (hari) → 1 baris per tanggal, Mulai=tgl & Selesai=tgl+1 → 1 hari/baris.
  //   DURATION_MONTHS → SATU baris sebulan penuh (tgl 1 s/d tgl terakhir). Jangan
  //     per tanggal: tiap baris = CEIL(hari/30) = 1 bulan, 30 baris = 30 bulan.
  //     Selesai = tgl terakhir (bukan tgl 1 bulan depan) agar 31 hari tak jadi 2 bulan.
  const rowDate = (e) => String(e.EntryDate || e.DateStart || "").slice(0, 10);
  const generateMonth = () => {
    const dates = monthDates(effMonth);
    if (!dates.length) { toast.error("Pilih bulan dulu"); return; }
    let baru;
    if (cfg.formula === "QTY_X_MULT") {
      // Office: SATU entri per periode bulan (EntryDate = tanggal 1).
      if (entries.some(e => rowDate(e).slice(0, 7) === effMonth)) {
        toast.info(`${monthLabel(effMonth)} sudah punya baris`);
        return;
      }
      baru = [{ ...blankRow(), EntryDate: `${effMonth}-01` }];
    } else if (cfg.formula === "DURATION_MONTHS") {
      if (entries.some(e => rowDate(e).slice(0, 7) === effMonth)) {
        toast.info(`${monthLabel(effMonth)} sudah punya baris`);
        return;
      }
      baru = [{ ...blankRow(), EntryDate: "", DateStart: dates[0], DateEnd: dates[dates.length - 1] }];
    } else {
      const sudahAda = new Set(entries.map(rowDate).filter(Boolean));
      const sisa = dates.filter(d => !sudahAda.has(d));
      if (!sisa.length) { toast.info("Semua tanggal di bulan itu sudah punya baris"); return; }
      baru = sisa.map(d => cfg.formula === "DURATION"
        ? { ...blankRow(), EntryDate: "", DateStart: d, DateEnd: nextDate(d) }
        : { ...blankRow(), EntryDate: d });
    }
    // Rumus durasi memakai Qty kosong = 1, jadi baris baru LANGSUNG bernilai tagihan.
    if (cfg.formula !== "QTY" && cfg.formula !== "QTY_X_MULT" && !window.confirm(
      `${baru.length} baris akan dibuat untuk ${itemLabel} (${monthLabel(effMonth)}).\n\n` +
      `Perhatian: Luas/Volume kosong dihitung 1, jadi baris ini langsung ikut tertagih. ` +
      `Isi luasnya, atau hapus baris yang tidak terpakai sebelum menyimpan.`
    )) return;
    setEntries(p => [...p, ...baru].sort((a, b) => rowDate(a).localeCompare(rowDate(b))));
    toast.success(`${baru.length} baris ditambahkan — tinggal isi ${cfg.formula === "QTY" ? "qty" : cfg.formula === "QTY_X_MULT" ? "ukuran & unit" : "luas/volume"}`);
  };

  // Baris yang tak jadi dipakai: Qty/Luas belum diisi & tanpa keterangan.
  const barisKosong = (e) => !num(e.Qty) && !(e.Remarks || "").trim();
  const jumlahKosong = isAreaRental ? entries.filter(barisKosong).length : 0;
  const hapusBarisKosong = () => {
    setEntries(p => p.filter(e => !barisKosong(e)));
    toast.success(`${jumlahKosong} baris kosong dihapus`);
  };

  const fileRef = useRef(null);
  const downloadTemplate = () => dlTemplate(cfg, itemLabel);
  const importExcel = (file) =>
    parseEntriesFromExcel(file, cfg, () => blankRow(defaultEntryDate(periodMonth, workDate)))
      // Dimensi tak diimpor langsung (read-only) — dihitung ulang dari L/H/W tiap baris.
      .then(rows => rows.map(r => ({ ...r, Dimensi: calcDimensi(r) })))
      .then(rows => { setEntries(rows); toast.success(`${rows.length} baris diimpor — periksa lalu klik Simpan Detail`); })
      .catch(err => toast.error("Gagal impor: " + (err?.message ?? err)));

  const total = entries.reduce((s, e) => s + calcBilledPR(cfg.formula, e), 0);

  const save = async () => {
    // Entri di luar bulan kerja JR tetap direkap MJS di bulan kerja, tapi
    // kemungkinan besar tanggalnya salah ketik / tertinggal default.
    const luar = periodMonth ? entries.filter(e => {
      const t = String(e.EntryDate || e.DateStart || "").slice(0, 7);
      return t && t !== periodMonth;
    }).length : 0;
    if (luar && !window.confirm(
      `${luar} baris bertanggal di luar bulan kerja JR (${periodMonth}).\n\nTetap simpan?`
    )) return;
    if (cfg.formula === "QTY_X_MULT") {
      const butuhUkuran = cfg.cols.some(c => c.key === "Multiplier");
      const periodeKosong = entries.some(e => !String(e.EntryDate || "").slice(0, 7));
      if (periodeKosong) { toast.error("Periode bulan wajib dipilih di setiap baris"); return; }
      const dobel = new Set(); let ada = false;
      entries.forEach(e => { const k = String(e.EntryDate).slice(0, 7); if (dobel.has(k)) ada = true; dobel.add(k); });
      if (ada) { toast.error("Satu periode bulan hanya boleh satu baris"); return; }
      if (butuhUkuran && entries.some(e => num(e.Qty) > 0 && !(num(e.Multiplier) > 0))) {
        toast.error("Ukuran (m²) wajib diisi pada baris yang punya Unit"); return;
      }
    }
    setSaving(true);
    const attrKeys = cfg.cols.filter(c => c.attr).map(c => c.key);
    const payload = entries.map(e => {
      const attributes = {};
      attrKeys.forEach(k => { if (e[k] != null && e[k] !== "") attributes[k] = e[k]; });
      // EntryDate eksplisit, atau diturunkan dari tanggal DateStart agar terekap di MJS.
      const entryDate = e.EntryDate || (e.DateStart ? String(e.DateStart).slice(0, 10) : "");
      return {
        EntryDate: entryDate,
        DateStart: e.DateStart || "",
        DateEnd: e.DateEnd || "",
        Qty: num(e.Qty),
        Dimensi: num(e.Dimensi),
        DimensiUom: e.DimensiUom || "",
        Length: num(e.Length),
        Height: num(e.Height),
        Weight: num(e.Weight),
        Multiplier: cfg.formula === "QTY_X_MULT" && cfg.cols.some(c => c.key === "Multiplier") ? (num(e.Multiplier) || 1) : 1,
        MeterStart: numOrNull(e.MeterStart),
        MeterEnd: numOrNull(e.MeterEnd),
        Uom: e.Uom || "",
        Remarks: e.Remarks || "",
        Attributes: Object.keys(attributes).length ? attributes : null,
      };
    });
    try {
      await dispatch(saveJrLineDetail({ JrID: jrID, JrLineID: line.JrLineID, Entries: payload })).unwrap();
      toast.success("Detail aktivitas tersimpan");
      onSaved && onSaved();
      load();
    } catch (e) {
      toast.error(typeof e === "string" ? e : (e?.message ?? "Gagal simpan detail"));
    } finally { setSaving(false); }
  };

  const renderCell = (col, e, i) => {
    switch (col.type) {
      case "label":
        return <span className="text-sm font-medium text-gray-700">{itemLabel}</span>;
      case "uom":
        return <span className="text-sm text-gray-500">{line.SatuanNama || line.SatuanID || "—"}</span>;
      case "computed":
        // Read-only — nilainya diturunkan otomatis (lihat calcDimensi / upd()).
        return <span className="text-sm text-gray-700 font-medium">{formatQty(e[col.key])}</span>;
      case "uomSelect":
        // Beda dari "uom" di atas yang cuma menampilkan satuan kontrak: Dimensi
        // memakai satuan sendiri, jadi harus benar-benar bisa dipilih.
        return (
          <select value={e[col.key] ?? ""} onChange={ev => upd(i, col.key, ev.target.value)}
            className="w-28 border rounded px-2 py-1 text-sm bg-white outline-none focus:border-blue-400">
            <option value="">—</option>
            {(uomOptions ?? []).map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        );
      case "date":
        // Backend mengembalikan tanggal format datetime ("YYYY-MM-DDTHH:MM");
        // input type=date butuh "YYYY-MM-DD" → potong 10 karakter agar tidak hilang saat reload.
        return <input type="date" value={String(e[col.key] ?? "").slice(0, 10)} onChange={ev => upd(i, col.key, ev.target.value)}
          className="w-40 border rounded px-2 py-1 text-sm outline-none focus:border-blue-400" />;
      case "month":
        // Periode bulan+tahun; disimpan sebagai tanggal 1 bulan itu (YYYY-MM-01).
        return <input type="month" value={String(e[col.key] ?? "").slice(0, 7)}
          onChange={ev => upd(i, col.key, ev.target.value ? `${ev.target.value}-01` : "")}
          className="w-40 border rounded px-2 py-1 text-sm outline-none focus:border-blue-400" />;
      case "select":
        return (
          <select value={e[col.key] ?? ""} onChange={ev => upd(i, col.key, ev.target.value)}
            className="w-32 border rounded px-2 py-1 text-sm bg-white outline-none focus:border-blue-400">
            <option value="">—</option>
            {col.options.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        );
      case "number":
        return <QtyInput value={e[col.key] ?? ""} onChange={v => upd(i, col.key, v)}
          className="w-28 border rounded px-2 py-1 text-right text-sm outline-none focus:border-blue-400" />;
      case "datetime-local":
        return <input type="datetime-local" value={e[col.key] ?? ""} onChange={ev => upd(i, col.key, ev.target.value)}
          className="w-48 border rounded px-2 py-1 text-sm outline-none focus:border-blue-400" />;
      default: // text
        return <input type="text" value={e[col.key] ?? ""} onChange={ev => upd(i, col.key, ev.target.value)}
          className="w-40 border rounded px-2 py-1 text-sm outline-none focus:border-blue-400" />;
    }
  };

  const wasRejected = !!line.RejectReason;

  return (
    <div className={`rounded-xl border p-4 ${wasRejected ? "border-red-300 bg-red-50/40" : "border-blue-100 bg-blue-50/30"}`}>
      {wasRejected && (
        <div className="mb-3 px-3 py-2 rounded-lg bg-red-100 border border-red-200 text-xs text-red-700">
          <span className="font-semibold">Sebelumnya ditolak klien — perlu revisi.</span> Alasan: {line.RejectReason}
        </div>
      )}
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className={`font-semibold text-sm ${wasRejected ? "text-red-700" : "text-gray-800"}`}>{itemLabel}</div>
          <div className="text-xs text-gray-500">{cfg.formula} · {line.SatuanNama || line.SatuanID}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {isAreaRental && (
            <div className="flex items-center gap-1 pr-2 mr-1 border-r border-blue-200">
              {periodMonth ? (
                <span title={`Periode JR ${periodMonth} — tidak bisa diubah di sini`}
                  className="border border-gray-200 bg-gray-100 text-gray-600 rounded-lg px-2 py-1 text-xs font-medium">
                  {monthLabel(periodMonth)}
                </span>
              ) : (
                <input type="month" value={genMonth}
                  onChange={ev => setGenMonth(ev.target.value)}
                  title="Bulan yang akan dibuatkan baris"
                  className="border rounded-lg px-2 py-1 text-xs outline-none focus:border-blue-400" />
              )}
              <button onClick={generateMonth}
                title={cfg.formula === "DURATION_MONTHS" || cfg.formula === "QTY_X_MULT"
                  ? `Buat satu baris sewa untuk periode ${monthLabel(effMonth)}`
                  : `Buat satu baris untuk setiap tanggal di ${monthLabel(effMonth)}`}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-blue-400 bg-blue-100 text-blue-700 text-xs font-semibold hover:bg-blue-200">
                <BiCalendarPlus /> Isi 1 Bulan
              </button>
              {jumlahKosong > 0 && (
                <button onClick={hapusBarisKosong}
                  title="Hapus baris yang Qty/Luas-nya belum diisi dan tanpa keterangan"
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-300 text-gray-600 text-xs font-medium hover:bg-gray-50">
                  <BiEraser /> Hapus Kosong ({jumlahKosong})
                </button>
              )}
            </div>
          )}
          <button onClick={downloadTemplate} title="Unduh template Excel sesuai format form ini"
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-300 text-gray-600 text-xs font-medium hover:bg-gray-50">
            <BiDownload /> Template
          </button>
          <button onClick={() => fileRef.current?.click()} title="Impor entri dari Excel"
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-green-300 text-green-700 text-xs font-medium hover:bg-green-50">
            <BiUpload /> Import
          </button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden"
            onChange={ev => { const f = ev.target.files?.[0]; if (f) importExcel(f); ev.target.value = ""; }} />
          <button onClick={addRow}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-blue-300 text-blue-700 text-xs font-medium hover:bg-blue-50">
            <BiPlus /> Tambah Entri
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-xs text-gray-400 py-3">Memuat entri…</div>
      ) : entries.length === 0 ? (
        <div className="text-xs text-gray-400 py-3">
          {isAreaRental
            ? `Belum ada entri. Klik “Isi 1 Bulan”${periodMonth ? ` untuk ${monthLabel(periodMonth)}` : ""}, atau “Tambah Entri” untuk satu baris.`
            : "Belum ada entri. Klik “Tambah Entri”."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500">
                {cfg.cols.map(c => <th key={c.key} className="px-2 py-1 font-semibold whitespace-nowrap">{c.label}</th>)}
                <th className="px-2 py-1 font-semibold text-right">Total</th>
                <th className="px-2 py-1" />
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => (
                <tr key={i} className="border-t border-blue-100">
                  {cfg.cols.map(c => <td key={c.key} className="px-2 py-1.5">{renderCell(c, e, i)}</td>)}
                  <td className="px-2 py-1.5 text-right font-semibold text-gray-800">{formatQty(calcBilledPR(cfg.formula, e))}</td>
                  <td className="px-2 py-1.5 text-right">
                    <button onClick={() => delRow(i)} className="text-red-400 hover:text-red-600 p-1"><BiTrash /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-blue-100">
        <div className="text-sm text-gray-600">
          Σ Total <strong className="text-gray-900">{formatQty(total)}</strong> {line.SatuanNama || line.SatuanID}
        </div>
        <button onClick={save} disabled={saving}
          className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-white text-sm font-semibold bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
          <BiSave /> {saving ? "Menyimpan…" : "Simpan Detail"}
        </button>
      </div>
    </div>
  );
}
